import React, { useState, useRef } from 'react';
import api from '../../../api';

const StudentActionBar = ({
  tenantId, // <-- add this
  searchQuery,
  onSearchChange,
  onSearch,
  onAddStudent,
  onBulkAdd,
  hasPrefix,
  onViewStudent,
  onEditStudent,
  onDeleteStudent,
  selectedCount,
  isViewOnly = false // Add this prop
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debounceRef = useRef();

  const handleSearchInputChange = async (e) => {
    const value = e.target.value;
    onSearchChange(value);
    setShowSuggestions(true);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!value.trim()) {
      setSuggestions([]);
      return;
    }

    // Debounce API call
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await api.get(
          `/api/students/search?tenantId=${tenantId}&query=${encodeURIComponent(value)}`
        );
        setSuggestions(res.data.students || []);
      } catch (err) {
        setSuggestions([]);
      }
    }, 300);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setShowSuggestions(false);
    onSearch();
  };


  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
        {/* Search Bar */}
        <div className="flex-1 min-w-[300px]">
          <form onSubmit={handleSubmit} className="relative">
            <div className="flex rounded-xl shadow-sm overflow-hidden">
              <input
                type="text"
                placeholder="Search students by name, email, or admission number..."
                value={searchQuery}
                onChange={handleSearchInputChange}
                onFocus={() => setShowSuggestions(true)}
                className="flex-1 min-w-0 block w-full px-4 py-3 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                autoComplete="off"
              />
              <button
                type="submit"
                className="inline-flex items-center px-4 py-3 border border-l-0 border-gray-300 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-medium hover:from-blue-600 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>
            </div>
            
            {/* Suggestions dropdown */}
            {showSuggestions && suggestions.length > 0 && (
              <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                {suggestions.map(s => (
                  <li
                    key={s.studentId || s._id}
                    className="px-4 py-3 hover:bg-blue-50 flex items-center border-b border-gray-100 last:border-b-0"
                  >
                    <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center mr-3">
                      {s.profilePhotoUrl ? (
                        <img src={s.profilePhotoUrl} alt="" className="h-10 w-10 rounded-full object-cover" />
                      ) : (
                        <span className="text-blue-600 text-sm font-semibold">
                          {s.firstName?.charAt(0)}{s.lastName?.charAt(0)}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 truncate">{s.firstName} {s.lastName}</div>
                      <div className="text-sm text-gray-500 truncate">{s.email}</div>
                      <div className="text-xs text-gray-400 mt-1">
                        {s.admissionNumber && `Adm: ${s.admissionNumber}`} 
                        {s.className && ` • ${s.className}`}
                        {s.rollNumber && ` • USN: ${s.rollNumber}`}
                      </div>
                    </div>
                    <div className="flex space-x-1 ml-2">
                      <button
                        type="button"
                        onClick={() => { onViewStudent && onViewStudent(s); setShowSuggestions(false); }}
                        className="p-1 text-gray-400 hover:text-blue-600"
                        title="View"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                      {!isViewOnly && (
                        <>
                          <button
                            type="button"
                            onClick={() => { onEditStudent && onEditStudent(s); setShowSuggestions(false); }}
                            className="p-1 text-gray-400 hover:text-yellow-600"
                            title="Edit"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            type="button"
                            onClick={() => { onDeleteStudent && onDeleteStudent(s.studentId || s._id); setShowSuggestions(false); }}
                            className="p-1 text-gray-400 hover:text-red-600"
                            title="Delete"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </form>
        </div>
        
        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
          {selectedCount > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-2 text-sm text-blue-700 font-medium">
              {selectedCount} selected
            </div>
          )}
          
          <div className="flex gap-3">
            {isViewOnly ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl px-4 py-3 text-sm text-yellow-700 font-medium flex items-center">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                View Only Mode
              </div>
            ) : (
              <>
                <button
                  onClick={onAddStudent}
                  disabled={!hasPrefix}
                  title={!hasPrefix ? "Please set both Admission-No Prefix and USN Prefix first" : undefined}
                  className={`inline-flex items-center px-5 py-3 rounded-xl font-medium transition-all duration-200 ${
                    !hasPrefix 
                      ? "bg-gray-200 text-gray-500 cursor-not-allowed" 
                      : "bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white shadow-sm hover:shadow-md"
                  }`}
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Add Student
                </button>
                <button
                  onClick={onBulkAdd}
                  disabled={!hasPrefix}
                  title={!hasPrefix ? "Please set both Admission-No Prefix and USN Prefix first" : undefined}
                  className={`inline-flex items-center px-5 py-3 rounded-xl font-medium transition-all duration-200 ${
                    !hasPrefix 
                      ? "bg-gray-200 text-gray-500 cursor-not-allowed" 
                      : "bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white shadow-sm hover:shadow-md"
                  }`}
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  Bulk Add
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Search Tips */}
      <div className="mt-4 pt-4 border-t border-gray-100">
        <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500">
          <span className="flex items-center">
            <svg className="w-3 h-3 mr-1 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            Search by name, email, or admission number
          </span>
          <span className="flex items-center">
            <svg className="w-3 h-3 mr-1 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            Use filters for more specific results
          </span>
          {isViewOnly && (
            <span className="flex items-center text-yellow-600">
              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              View Only: Add/Edit disabled for completed academic years
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentActionBar;